import { useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { toast } from 'sonner'

interface Props {
  /** Where to send the user. Default `/`. */
  to?: string
  /** Toast title — short, action-oriented. */
  title: string
  /** Optional secondary line below the title (role, reason, etc.). */
  description?: string | undefined
  /** Toast variant. Default `'error'`. */
  variant?: 'error' | 'info'
  /** Toast duration in ms. Default 4000. */
  duration?: number
}

/**
 * `<Navigate>` with a sonner toast announcement.
 *
 * Replaces silent `<Navigate to="/" replace />` patterns where the user
 * tried to reach a page they couldn't access (no permission, unknown
 * route, etc.). Without a toast, the user lands on the dashboard with
 * no idea why their bookmark / typed URL stopped working — leading to
 * confusion, repeated attempts, and support pings.
 *
 * Toast fires once per pathname via a ref guard, so re-renders during
 * the redirect (e.g. role query refresh) don't multi-fire. Mirrors the
 * pattern already used in `RoleGate`.
 *
 * Usage:
 *
 *   if (!isAdmin) {
 *     return (
 *       <RedirectWithToast
 *         title="Solo administradores"
 *         description="Esta sección es para el equipo de uPadelMatch."
 *       />
 *     )
 *   }
 */
export function RedirectWithToast({
  to = '/',
  title,
  description,
  variant = 'error',
  duration = 4000,
}: Props) {
  const location = useLocation()
  const firedFor = useRef<string | null>(null)

  useEffect(() => {
    if (firedFor.current === location.pathname) return
    firedFor.current = location.pathname
    const opts = description ? { description, duration } : { duration }
    if (variant === 'error') {
      toast.error(title, opts)
    } else {
      toast.info(title, opts)
    }
  }, [location.pathname, title, description, variant, duration])

  return <Navigate to={to} replace />
}
